// orphaned-ticket-liveness-readers.ts
//
// Split out of orphaned-ticket-reconciler.ts (197/200 lines) for phase 06g
// R2b: the two on-disk liveness readers the reconciler and `doctor` share —
// the binding lease, and the active-worker record checked against real
// process liveness on THIS host.
import { promises as fs } from "node:fs";
import { hostname as osHostname } from "node:os";
import { join } from "node:path";
import { type ActiveWorkerRecord, readActiveWorker } from "./active-worker-record.js";
import {
  deadOrPidReused,
  type LockLivenessProbe,
  realLivenessProbe,
} from "./process-liveness-probe.js";

export interface OrphanedTicketBindingLease {
  expires_at: string;
}

export type OrphanedTicketBindingLeaseRead =
  | OrphanedTicketBindingLease
  | { corrupt: true }
  | undefined;

/** Shared with orphaned-ticket-recovery-applier.ts — one source of truth for the binding-file path. */
export function bindingPathFor(missionWorkspace: string): string {
  return join(missionWorkspace, ".claude", "state", "autopilot-binding.json");
}

/**
 * Review 260909-p6-m2: `undefined` = no file at all (supervisor_crash path);
 * `{ corrupt: true }` = file exists but is not the expected shape — UNSAFE to
 * reclaim (tampered? partial write?), never "no binding".
 */
export async function defaultReadBindingLease(
  missionWorkspace: string,
): Promise<OrphanedTicketBindingLeaseRead> {
  let raw: string;
  try {
    raw = await fs.readFile(bindingPathFor(missionWorkspace), "utf8");
  } catch {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<OrphanedTicketBindingLease>;
    if (typeof parsed.expires_at === "string") return { expires_at: parsed.expires_at };
    return { corrupt: true };
  } catch {
    return { corrupt: true };
  }
}

export interface ActiveWorkerLivenessDeps {
  readActiveWorker?: (controlRoot: string) => Promise<ActiveWorkerRecord | undefined>;
  liveness?: LockLivenessProbe;
  hostname?: () => string;
}

export type ActiveWorkerLiveness =
  | { state: "no_record" }
  | { state: "foreign_host"; record: ActiveWorkerRecord }
  | { state: "alive"; record: ActiveWorkerRecord }
  | { state: "dead"; record: ActiveWorkerRecord };

/**
 * 06g R2b (debate B1/N4): "dead" only when BOTH the supervisor that spawned
 * the worker AND the worker are dead-or-pid-reused — a detached worker
 * outlives a SIGKILLed supervisor. A record from another host (or without a
 * hostname, written before this field existed) proves nothing here.
 */
export async function probeActiveWorkerLiveness(
  controlRoot: string,
  deps: ActiveWorkerLivenessDeps = {},
): Promise<ActiveWorkerLiveness> {
  const record = await (deps.readActiveWorker ?? readActiveWorker)(controlRoot);
  if (record === undefined) return { state: "no_record" };
  if (record.hostname === undefined || record.hostname !== (deps.hostname ?? osHostname)()) {
    return { state: "foreign_host", record };
  }
  const liveness = deps.liveness ?? realLivenessProbe;
  const supervisorDead = await deadOrPidReused(
    liveness,
    record.supervisor_pid,
    record.supervisor_started_at,
  );
  const workerDead = await deadOrPidReused(liveness, record.pid, record.worker_started_at);
  return { state: supervisorDead && workerDead ? "dead" : "alive", record };
}
